'use client';

import Link from 'next/link';

export default function DisclaimerPage() {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&display=swap');
        .policy-root {
          font-family: 'DM Sans', sans-serif;
          background: #f8f9fb;
          min-height: 100vh;
          color: #1a1a2e;
        }
        .policy-nav {
          background: #0d1b2a;
          padding: 0 32px;
          height: 68px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          position: sticky;
          top: 0;
          z-index: 100;
        }
        .policy-logo {
          display: flex;
          align-items: center;
          gap: 10px;
          text-decoration: none;
        }
        .policy-logo-icon {
          width: 38px;
          height: 38px;
          background: linear-gradient(135deg, #00b4d8, #0077b6);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .policy-logo-text { font-size: 20px; font-weight: 700; color: #ffffff; }
        .policy-logo-text span { color: #00b4d8; }
        .policy-back {
          color: #00b4d8;
          text-decoration: none;
          font-size: 14px;
          font-weight: 500;
          transition: opacity 0.2s;
        }
        .policy-back:hover { opacity: 0.8; }
        .policy-hero {
          background: linear-gradient(135deg, #0d1b2a 0%, #1a3a5c 100%);
          padding: 60px 32px;
          text-align: center;
        }
        .policy-hero h1 { font-size: 40px; font-weight: 700; color: #fff; margin: 0 0 12px; }
        .policy-hero p { color: #8b9ab0; font-size: 15px; margin: 0; }
        .policy-content { max-width: 860px; margin: 0 auto; padding: 60px 32px; }
        .policy-card {
          background: #fff;
          border-radius: 16px;
          padding: 48px;
          border: 1px solid #e8ecf0;
          box-shadow: 0 4px 24px rgba(0,0,0,0.04);
        }
        .disclaimer-alert {
          background: #fff8e1;
          border: 1.5px solid #f59e0b;
          border-radius: 12px;
          padding: 20px 24px;
          margin-bottom: 32px;
          display: flex;
          gap: 14px;
          align-items: flex-start;
        }
        .disclaimer-alert-icon {
          font-size: 22px;
          flex-shrink: 0;
          margin-top: 2px;
        }
        .disclaimer-alert p {
          font-size: 15px;
          color: #92400e;
          margin: 0;
          line-height: 1.6;
          font-weight: 500;
        }
        .policy-card h2 {
          font-size: 22px;
          font-weight: 700;
          color: #0d1b2a;
          margin: 32px 0 12px;
          padding-top: 32px;
          border-top: 1px solid #f0f4f8;
        }
        .policy-card h2:first-of-type { margin-top: 0; padding-top: 0; border-top: none; }
        .policy-card p, .policy-card li { font-size: 15px; line-height: 1.8; color: #4b5563; margin: 0 0 12px; }
        .policy-card ul { padding-left: 20px; margin: 0 0 16px; }
        .policy-card li { margin-bottom: 8px; }
        .policy-updated {
          display: inline-block;
          background: rgba(0,180,216,0.1);
          border: 1px solid rgba(0,180,216,0.3);
          color: #00b4d8;
          font-size: 13px;
          font-weight: 600;
          padding: 6px 14px;
          border-radius: 99px;
          margin-bottom: 32px;
        }
        .policy-footer {
          background: #0d1b2a;
          padding: 24px 32px;
          text-align: center;
          color: #8b9ab0;
          font-size: 13px;
        }
        .policy-footer a { color: #00b4d8; text-decoration: none; }
        .policy-footer a:hover { text-decoration: underline; }
      `}</style>

      <div className="policy-root">
        <nav className="policy-nav">
          <Link href="/" className="policy-logo">
            <div className="policy-logo-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="4" fill="#fff" />
                <circle cx="12" cy="12" r="9" stroke="#fff" strokeWidth="2" fill="none" />
              </svg>
            </div>
            <span className="policy-logo-text">Cam<span>Smart</span></span>
          </Link>
          <Link href="/" className="policy-back">← Back to Store</Link>
        </nav>

        <div className="policy-hero">
          <h1>Disclaimer</h1>
          <p>Important information about our products, services, and website content</p>
        </div>

        <div className="policy-content">
          <div className="policy-card">
            <span className="policy-updated">Last Updated: May 9, 2026</span>

            <div className="disclaimer-alert">
              <span className="disclaimer-alert-icon">⚠️</span>
              <p>The information provided on this website is for general informational purposes only. CamSmart makes no representations or warranties of any kind, express or implied, about the completeness, accuracy, reliability, or suitability of the information, products, or services contained on this website.</p>
            </div>

            <h2>General Disclaimer</h2>
            <p>CamSmart provides security cameras, doorbells, and related accessories for surveillance and monitoring purposes. While our products are designed to enhance security, we cannot guarantee that use of our products will prevent crime, theft, or other security incidents. Our products are tools to assist in security monitoring and should be used as part of a comprehensive security strategy.</p>

            <h2>Product Performance Disclaimer</h2>
            <p>Product specifications, including resolution, range, battery life, and connectivity, are based on manufacturer testing under controlled conditions. Actual performance may vary depending on:</p>
            <ul>
              <li>Environmental conditions (weather, lighting, temperature)</li>
              <li>Network speed and reliability</li>
              <li>Device compatibility and software versions</li>
              <li>Installation location and configuration</li>
              <li>Interference from other electronic devices</li>
            </ul>

            <h2>Legal Use Disclaimer</h2>
            <p>It is your sole responsibility to ensure that your use of our security products complies with all applicable local, state, and federal laws and regulations. This includes but is not limited to:</p>
            <ul>
              <li>Privacy laws regarding recording individuals without consent</li>
              <li>Laws governing surveillance in public and private spaces</li>
              <li>Homeowner association rules and local ordinances</li>
              <li>Tenant and landlord regulations</li>
            </ul>
            <p>CamSmart is not responsible for any legal consequences arising from improper or illegal use of our products.</p>

            <h2>Third-Party Links & Content</h2>
            <p>Our website may contain links to third-party websites. These links are provided for your convenience only. CamSmart has no control over the content of those sites and accepts no responsibility for them or for any loss or damage that may arise from your use of them.</p>

            <h2>Advertising Disclaimer</h2>
            <p>This website uses Google AdSense and other advertising networks to display advertisements. The appearance of advertisements does not constitute endorsement of the advertised products or services by CamSmart. We are not responsible for the content of external advertisements.</p>

            <h2>Affiliate Disclaimer</h2>
            <p>Some links on this website may be affiliate links. If you click on an affiliate link and make a purchase, CamSmart may receive a commission at no additional cost to you. We only recommend products and services we believe will add value to our customers.</p>

            <h2>No Professional Advice</h2>
            <p>The content on this website does not constitute professional security, legal, or technical advice. For specific security needs, we recommend consulting with a licensed security professional. For legal questions regarding surveillance laws in your area, please consult a qualified attorney.</p>

            <h2>Limitation of Liability</h2>
            <p>In no event shall CamSmart, its directors, employees, or agents be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from your use of our products or website.</p>

            <h2>Contact Us</h2>
            <p>If you have questions about this Disclaimer, please contact us at:<br /><strong>CamSmart</strong><br />Email: legal@camsmart.com<br />Phone: 1-800-CAM-SMART</p>
          </div>
        </div>

        <footer className="policy-footer">
          <p>© 2026 CamSmart. All rights reserved. &nbsp;|&nbsp; <Link href="/privacy-policy">Privacy Policy</Link> &nbsp;|&nbsp; <Link href="/terms-of-service">Terms of Service</Link> &nbsp;|&nbsp; <Link href="/disclaimer">Disclaimer</Link></p>
        </footer>
      </div>
    </>
  );
}
